<template>
  <div>
    <!-- Framework control table -->
    <div class="card pagination-margin" qid="activities-section">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <div class="float-left">
              <h5 class="mb-2">{{ $t('maintenance.policies.framework_table_title') }}</h5>
              <span class="cursor-pointer task-label-open mr-2"
                    v-if="getTask('update_activities')"
                    @click="openToolbar(getTask('update_activities'))"
              >
                <font-awesome-icon icon="info-circle" class="text-primary"/>
                {{ $t('tasks.open_label') }}
              </span>
              <span v-if="getGuide('update_activities')">
                <GuideButton :guide="getGuide('update_activities')"></GuideButton>
              </span>
            </div>
          </div>
          <div class="col-sm-6 text-right">
            <button class="btn btn-success ml-3 pl-3 pr-3"
                    @click="showCreateSModal()"
                    v-if="isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], policy) && (!getClient.parent || (getClient.parent && policy.partition))"
                    qid="measure-system-create-button"
            >
              <font-awesome-icon icon="plus"/> {{ $t('maintenance.policies.add_button') }}
            </button>
          </div>
        </div>
        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col">
              {{ $t('maintenance.framework_control.table.name') }}
            </th>
            <th scope="col">
              {{ $t('generic_controls.framework_controls.framework') }}
            </th>
            <th class="text-center">
              {{ $t('maintenance.framework_control.table.applicable') }}
            </th>
            <th class="">
              {{ $t('maintenance.framework_control.table.implementation_status') }}
            </th>
            <th scope="col">
              {{ $t('system.last_updated') }}
            </th>
            <th width="130"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="(object, index) in tableItems">
              <td>
                <span v-if="object.framework_control">
                  {{ object.framework_control.full_name }}
                </span>
              </td>
              <td>
                <span class="mr-1" v-if="object.framework_control && object.framework_control.active_framework" qid="object-framework">
                  <span class="badge mb-1" :title="object.framework_control.active_framework.name">
                    {{ object.framework_control.active_framework.name | truncate(35) }}
                  </span>
                </span>
              </td>
              <td class="text-center">
                <span v-if="object.framework_control">
                  <span v-if="object.framework_control.statement_of_applicability">
                    <font-awesome-icon icon="check-circle" class="icon-success"/>
                    {{ $t('system.yes') }}
                  </span>
                  <span v-if="!object.framework_control.statement_of_applicability">
                    <font-awesome-icon icon="times-circle" class="icon-danger"/>
                    {{ $t('system.no') }}
                  </span>
                </span>
              </td>
              <td>
                <span v-if="object.framework_control && object.framework_control.implementation_status">
                  {{ $t('maintenance.policies.status.' + $options.filters.slugify(object.framework_control.implementation_status)) }}
                </span>
              </td>
              <td>
                <span class="text-normal" v-if="object.framework_control">
                  {{ object.framework_control.updated_at | formatDate }}
                </span>
              </td>
              <td>
                <button v-if="object.framework_control && isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], policy) && (!getClient.parent || (getClient.parent && policy.partition))"
                        class="btn btn-delete"
                        qid="delete-p-modal"
                        @click="showConfirmDeleteModal(object)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </button>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card-body">
        <div class="pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
    </div>


    <!-- Add new framework modal -->
    <b-modal ref="add-framework" hide-footer @hide="hideCreateSModal">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{ $t('maintenance.policies.create_framework_modal_title') }}
        </h4>
      </div>
      <b-alert v-if="policyError.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ policyError }}
      </b-alert>
      <b-form @submit="sFormSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-framewor-control-pg-label">
              {{ $t('generic_controls.framework_controls.create.framework_label') }}
            </label>
            <multiselect
                v-model="selectedFramework"
                :options="frameworkItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('generic_controls.framework_controls.create.framework_placeholder')"
                label="name"
                @search-change="loadFrameworkItems"
                @select="handleFrameworkChange"
                track-by="slug"
                qid="create-lb-form-pg"
            ></multiselect>
          </b-form-group>
          <b-form-group v-if="selectedFramework && selectedFramework.id">
            <label qid="create-ac-pg-label">
              {{ $t('generic_controls.framework_controls.create.framework_control_label') }}
            </label>
            <multiselect
                v-model="selectedP"
                :options="pItems"
                :placeholder="$t('generic_controls.framework_controls.create.framework_control_placeholder')"
                label="full_name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadFrameworkControlItems"
                track-by="slug"
                qid="create-ac-form-ac"
            ></multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateSModal"
                    qid="create-ac-cancel-button"
          >
            {{ $t('activities.process.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    :disabled="buttonDisabled"
                    variant="success"
                    qid="create-ac-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('activities.process.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Delete framework confirm modal template -->
    <b-modal ref="delete-framework" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-tp-title">
          {{ $t('maintenance.policies.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.policies.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.policies.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-tp-cancel-button"
        >
          {{ $t('maintenance.policies.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeletePolicy(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-tp-submit-button"
                  v-if="isGranted([permissions.POLICY_EDIT, permissions.POLICY_EDIT_OWN], policy) && (!getClient.parent || (getClient.parent && policy.partition))"
        >
          {{ $t('maintenance.policies.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import roleCheck from "@/util/ACL/roleCheck"

  import Avatar from '@/components/Avatar.vue';
  import PartitionNoticeModal from '@/components/PartitionNoticeModal.vue';
  import PolicyViewModal from '@/views/Policies/PolicyViewModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'FrameworkControlsTable',
    data: function() {
      return {

        // Systems
        tableItems: [],
        selectedS: [],
        viewSModal: false,
        selectedSSort: '',
        selectedSDirection: '',
        pItems: [],
        sLoading: true,
        policy: {},

        error: '',
        policyError: '',
        loadingItems: false,
        itemToDelete: {},
        itemObject: {},
        selectedP: [],
        showSDescription: false,

        currentPage: 1,
        totalRows: 0,
        selectedSort: '',
        selectedDirection: '',
        keywordValue: '',
        myGuides: [],
        buttonDisabled: false,
        frameworkItems: [],
        selectedFramework: [],

        loading: true,
        perPage: '',
        selectedPerPage: "10",
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' },
        ],
        showNoticeModal: false,
        selectedItem: {},
        policyToView: {},
        viewPolicyModal: false
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      }
    },
    components: {
      Avatar,
      PartitionNoticeModal,
      PolicyViewModal,
      GuideButton
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getUser() {
        return this.$store.state.user
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when prop changes and then load validations
      object: function (value) {
        if (value) {
          this.policy = value
        }
        //this.loadContent()
      }
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.tasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      isOwner(object) {
        let self = this;
        let allow = false;
        let existingUser = _.find(object.users, function(user) {
          return user.id == self.getUser.id
        });

        if (existingUser) {
          allow = true
        } else {
          if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
            allow = true
          }
        }

        return allow
      },
      openViewPolicyModal(object) {
        this.policyToView = object
        this.viewPolicyModal = true
      },
      handleCloseViewPolicyModal () {
        this.policyToView = []
        this.viewPolicyModal = false;
      },
      openNoticeModal(object) {
        this.selectedItem = object
        this.showNoticeModal = true
      },
      closeNoticeModal() {
        this.selectedItem = {}
        this.showNoticeModal = false
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Policy' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Policy',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-framework'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-framework'].hide()
      },
      openViewSModal(object) {
        this.selectedS = object
        this.viewSModal = true
      },
      handleCloseViewSModal () {
        this.selectedS = []
        this.viewSModal = false;
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;

        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
        // Load Third parties when page changed listener is triggered
        this.loadContent(1, perPage.value)
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showCreateSModal() {
        this.loadFrameworkItems();
        this.$refs['add-framework'].show()
      },
      hideCreateSModal() {
        this.policyError = ''
        this.selectedP = []
        this.selectedFramework = []
        this.loadContent()
        this.$refs['add-framework'].hide()
      },
      sFormSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        formData.append("framework_control_id", this.selectedP.id);

        if (this.selectedP && this.selectedP.id) {
          piincHttp.post('/policies/' + this.$route.params.slug + '/framework-controls', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.$toastr('success', self.$t('maintenance.policies.create.framework_success'))
            self.hideCreateSModal();
            self.loadContent();
            self.buttonDisabled = false;
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.policyError = error.data.message;
              if (error.data.errors.framework_control_id) {
                self.policyError += ' ' + error.data.errors.framework_control_id[0]
              }
            }
          })
        } else {
          this.policyError = ''
          this.buttonDisabled = false;
          if (!this.selectedP || (this.selectedP && !this.selectedP.id)) {
            this.policyError +=  ' ' + this.$t('maintenance.policies.create.framework_error')
          }
        }
      },
      loadFrameworkControlItems(query) {
        let self = this;
        piincHttp.get('framework-controls', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            statement_of_applicability: 1,
            framework: this.selectedFramework.id,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.pItems = response.data.items
        }, function() {});
      },
      loadFrameworkItems(query) {
        let self = this;
        piincHttp.get('frameworks', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.frameworkItems = response.data.items
          if (self.frameworkItems && !self.frameworkItems.length && !query) {
            self.frameworkItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      handleFrameworkChange(item) {
        this.selectedFramework = item
        this.loadFrameworkControlItems()
      },
      loadContent(page, perPage) {
        let self = this;

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;

        piincHttp.get('policies/' + this.$route.params.slug + '/framework-controls', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            client: this.getClient.slug
          }
        }).then(function (response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function () {
        });

      },
      confirmDeletePolicy(object) {
        let self = this;
        piincHttp.delete('policies/' + this.$route.params.slug + '/framework-controls/' + object.id).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.policies.create.framework_delete_success'))
        }, function() {});
      },
    },
    created () {
      // Table items
      this.policy = this.object;
      this.loadContent();
      // this.loadGuides('update_activities');
    }
  }
</script>
